import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ResponsiveDialog, ResponsiveDialogContent, ResponsiveDialogHeader, ResponsiveDialogTitle } from "@/components/ui/responsive-dialog";
import { ResponsiveDialogTitle as ResponsiveDialogTitleComponent, ResponsiveDialogTitleContent, ResponsiveDialogTitleHeader, ResponsiveDialogTitleTitle } from "@/components/ui/responsive-dialog-title";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCurrentLanguageInClient, getValueFromTranslations } from "@/lib/utils.client";
import type { UserCouponWithTemplate } from "./schema";
interface CouponRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  coupon: UserCouponWithTemplate;
  code: string;
  remark?: string;
}
const CouponRulesDialog: React.FC<CouponRulesDialogProps> = t0 => {
  const $ = _c(103);
  const {
    open,
    onOpenChange,
    coupon,
    code
  } = t0;
  const {
    t
  } = useTranslation();
  const isMobile = useIsMobile();
  let t1;
  if ($[0] !== coupon.couponTemplate?.description_trans || $[1] !== t) {
    const lang = getCurrentLanguageInClient();
    t1 = getValueFromTranslations(lang, coupon.couponTemplate?.description_trans) || t("coupon.noRulesAvailable", "No rules available");
    $[0] = coupon.couponTemplate?.description_trans;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const rules = t1;
  if (isMobile) {
    let t2;
    if ($[3] !== t) {
      t2 = t("coupon.rules.title", "Coupon Rules");
      $[3] = t;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t2) {
      t3 = <ResponsiveDialogTitleHeader className="!pt-0"><ResponsiveDialogTitleTitle className="font-['DINPro-Bold'] font-Bold text-sm uppercase">{t2}</ResponsiveDialogTitleTitle></ResponsiveDialogTitleHeader>;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t) {
      t4 = t("coupon.code");
      $[7] = t;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== code) {
      t5 = <span className="break-all">{code}</span>;
      $[9] = code;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== t4 || $[12] !== t5) {
      t6 = <div className="text-xs font-normal text-gray-900 font-['DINPro-Regular']">{t4}: {t5}</div>;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <span className="inline-block w-0.5 h-0.5 bg-gray-700 rounded-full mr-2" />;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== coupon.couponTemplate?.applicableProductsRule || $[16] !== t) {
      t8 = coupon.couponTemplate?.applicableProductsRule === "all" ? t("coupon.applicableToAllItems", "Applicable to all items") : t("coupon.applicableToSelectItems", "Applicable to select items only");
      $[15] = coupon.couponTemplate?.applicableProductsRule;
      $[16] = t;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== t8) {
      t9 = <div className="text-xs font-normal text-gray-700 flex items-center font-['DINPro-Regular']">{t7}{t8}</div>;
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <span className="inline-block w-0.5 h-0.5 bg-gray-700 rounded-full mr-2" />;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== t) {
      t11 = t("coupon.appliedTo", "Applied to");
      $[21] = t;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== coupon.platformChannels) {
      t12 = coupon.platformChannels?.join(", ") || "N/A";
      $[23] = coupon.platformChannels;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== t11 || $[26] !== t12) {
      t13 = <div className="text-xs font-normal text-gray-700 flex items-center font-['DINPro-Regular']">{t10}{t11}:{" "}{t12}</div>;
      $[25] = t11;
      $[26] = t12;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    let t14;
    if ($[28] !== t13 || $[29] !== t9) {
      t14 = <div className="flex flex-col gap-1 font-['DINPro-Regular']">{t9}{t13}</div>;
      $[28] = t13;
      $[29] = t9;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    let t15;
    if ($[31] !== t) {
      t15 = t("coupon.remark", "Remark");
      $[31] = t;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    let t16;
    if ($[33] !== t15) {
      t16 = <span className="font-normal">{t15}:</span>;
      $[33] = t15;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    let t17;
    if ($[35] !== rules || $[36] !== t16) {
      t17 = <div className="text-xs font-normal whitespace-pre-wrap text-gray-700 font-['DINPro-Regular']">{t16}{" "}{rules}</div>;
      $[35] = rules;
      $[36] = t16;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    let t18;
    if ($[38] !== t14 || $[39] !== t17 || $[40] !== t6) {
      t18 = <div className="flex flex-col gap-4">{t6}{t14}{t17}</div>;
      $[38] = t14;
      $[39] = t17;
      $[40] = t6;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    let t19;
    if ($[42] !== t18 || $[43] !== t3) {
      t19 = <ResponsiveDialogTitleContent className="max-h-58! p-4! [&_.absolute]:!right-4 [&_.absolute]:!top-4">{t3}{t18}</ResponsiveDialogTitleContent>;
      $[42] = t18;
      $[43] = t3;
      $[44] = t19;
    } else {
      t19 = $[44];
    }
    let t20;
    if ($[45] !== onOpenChange || $[46] !== open || $[47] !== t19) {
      t20 = <ResponsiveDialogTitleComponent open={open} onOpenChange={onOpenChange}>{t19}</ResponsiveDialogTitleComponent>;
      $[45] = onOpenChange;
      $[46] = open;
      $[47] = t19;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    return t20;
  }
  let t2;
  if ($[49] !== t) {
    t2 = t("coupon.rules.title", "Coupon Rules");
    $[49] = t;
    $[50] = t2;
  } else {
    t2 = $[50];
  }
  let t3;
  if ($[51] !== t2) {
    t3 = <ResponsiveDialogHeader className="!pt-0"><ResponsiveDialogTitle className="text-center font-['DINPro-Medium'] font-medium! text-2xl! uppercase">{t2}</ResponsiveDialogTitle></ResponsiveDialogHeader>;
    $[51] = t2;
    $[52] = t3;
  } else {
    t3 = $[52];
  }
  let t4;
  if ($[53] !== t) {
    t4 = t("coupon.code");
    $[53] = t;
    $[54] = t4;
  } else {
    t4 = $[54];
  }
  let t5;
  if ($[55] !== code) {
    t5 = <span className="font-mono break-all">{code}</span>;
    $[55] = code;
    $[56] = t5;
  } else {
    t5 = $[56];
  }
  let t6;
  if ($[57] !== t4 || $[58] !== t5) {
    t6 = <div className="text-base font-normal text-gray-900 font-['DINPro-Regular']">{t4}:{" "}{t5}</div>;
    $[57] = t4;
    $[58] = t5;
    $[59] = t6;
  } else {
    t6 = $[59];
  }
  let t7;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="inline-block w-0.5 h-0.5 bg-gray-700 rounded-full mr-2" />;
    $[60] = t7;
  } else {
    t7 = $[60];
  }
  let t8;
  if ($[61] !== coupon.couponTemplate?.applicableProductsRule || $[62] !== t) {
    t8 = coupon.couponTemplate?.applicableProductsRule === "all" ? t("coupon.applicableToAllItems", "Applicable to all items") : t("coupon.applicableToSelectItems", "Applicable to select items only");
    $[61] = coupon.couponTemplate?.applicableProductsRule;
    $[62] = t;
    $[63] = t8;
  } else {
    t8 = $[63];
  }
  let t9;
  if ($[64] !== t8) {
    t9 = <div className="text-base font-normal text-gray-700 flex items-center font-['DINPro-Regular']">{t7}{t8}</div>;
    $[64] = t8;
    $[65] = t9;
  } else {
    t9 = $[65];
  }
  let t10;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="inline-block w-0.5 h-0.5 bg-gray-700 rounded-full mr-2" />;
    $[66] = t10;
  } else {
    t10 = $[66];
  }
  let t11;
  if ($[67] !== t) {
    t11 = t("coupon.appliedTo", "Applied to");
    $[67] = t;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  let t12;
  if ($[69] !== coupon.platformChannels) {
    t12 = coupon.platformChannels?.join(", ") || "N/A";
    $[69] = coupon.platformChannels;
    $[70] = t12;
  } else {
    t12 = $[70];
  }
  let t13;
  if ($[71] !== t11 || $[72] !== t12) {
    t13 = <div className="text-base font-normal text-gray-700 flex items-center font-['DINPro-Regular']">{t10}{t11}:{" "}{t12}</div>;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  let t14;
  if ($[74] !== t13 || $[75] !== t9) {
    t14 = <div className="flex flex-col gap-1 font-['DINPro-Regular']">{t9}{t13}</div>;
    $[74] = t13;
    $[75] = t9;
    $[76] = t14;
  } else {
    t14 = $[76];
  }
  let t15;
  if ($[77] !== t) {
    t15 = t("coupon.remark", "Remark");
    $[77] = t;
    $[78] = t15;
  } else {
    t15 = $[78];
  }
  let t16;
  if ($[79] !== t15) {
    t16 = <span className="font-normal">{t15}:</span>;
    $[79] = t15;
    $[80] = t16;
  } else {
    t16 = $[80];
  }
  let t17;
  if ($[81] !== rules || $[82] !== t16) {
    t17 = <div className="text-base font-normal whitespace-pre-wrap text-gray-700 font-['DINPro-Regular']">{t16}{" "}{rules}</div>;
    $[81] = rules;
    $[82] = t16;
    $[83] = t17;
  } else {
    t17 = $[83];
  }
  let t18;
  if ($[84] !== onOpenChange) {
    t18 = () => onOpenChange(false);
    $[84] = onOpenChange;
    $[85] = t18;
  } else {
    t18 = $[85];
  }
  let t19;
  if ($[86] !== t) {
    t19 = t("common.confirm", "Confirm");
    $[86] = t;
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  let t20;
  if ($[88] !== t18 || $[89] !== t19) {
    t20 = <Button className="mt-2 w-full uppercase" onClick={t18}>{t19}</Button>;
    $[88] = t18;
    $[89] = t19;
    $[90] = t20;
  } else {
    t20 = $[90];
  }
  let t21;
  if ($[91] !== t14 || $[92] !== t17 || $[93] !== t20 || $[94] !== t6) {
    t21 = <div className="flex flex-col gap-4">{t6}{t14}{t17}{t20}</div>;
    $[91] = t14;
    $[92] = t17;
    $[93] = t20;
    $[94] = t6;
    $[95] = t21;
  } else {
    t21 = $[95];
  }
  let t22;
  if ($[96] !== t21 || $[97] !== t3) {
    t22 = <ResponsiveDialogContent className="max-h-[357px]! max-w-[460px]! p-10">{t3}{t21}</ResponsiveDialogContent>;
    $[96] = t21;
    $[97] = t3;
    $[98] = t22;
  } else {
    t22 = $[98];
  }
  let t23;
  if ($[99] !== onOpenChange || $[100] !== open || $[101] !== t22) {
    t23 = <ResponsiveDialog open={open} onOpenChange={onOpenChange}>{t22}</ResponsiveDialog>;
    $[99] = onOpenChange;
    $[100] = open;
    $[101] = t22;
    $[102] = t23;
  } else {
    t23 = $[102];
  }
  return t23;
};
export default CouponRulesDialog;